import { normalizeHex } from '@/utils/helpers'
import {
  CartProduct,
  CartProductFromStorage,
  Pallete,
  QrMenuCheckDetails,
  QrMenuCheckDetailsType,
  QrMenuCheckProduct,
  QrMenuCreateOrderPayload,
  QrMenuOrderItem,
  QrMenuTableOrderPayload,
  QrMenuFastOrderApiOrderItem,
  QrMenuFastOrderPayment,
  QrMenuFastOrderResponseData,
  QrMenuModifier,
  QrMenuOrderType,
  QrMenuProduct
} from '@/composables/qr-menu/types'

export const setQrMenuPallete = (data?: Partial<Pallete>): Pallete => {
  return {
    theme: data?.theme ?? 'light',
    primaryColor: normalizeHex(data?.primaryColor) ?? '#f05039',
    bgColor: data?.theme === 'dark' ? '#121212' : 'var(--white)',
    secondaryColor: data?.theme === 'dark' ? '#3E3E3E' : '#fbfbfb',
    productCardColor: data?.theme === 'dark' ? '#1D1D1D' : '#f4f4f4',
    textColor: data?.theme === 'dark' ? '#fff' : '#000',
    secondaryTextColor: data?.theme === 'dark' ? '#aaa' : 'var(--neutral-text)',
    borderColor: data?.theme === 'dark' ? '#3E3E3E' : '#dfdfdf',
    priceBlockColor: data?.theme === 'dark' ? '#3E3E3E' : '#f4f4f4'
  }
}


export const buildCartKey = (
  product: QrMenuProduct,
  selectedModifiers: Array<{ id: number; amount: number }>
): string => {
  const normalized = (selectedModifiers || [])
    .filter(m => (m?.amount ?? 0) > 0)
    .sort((a, b) => a.id - b.id)
    .map(m => `${m.id}:${m.amount}`)
    .join(',')

  return normalized ? `${product.id}::${normalized}` : `${product.id}`
}

/** Все ключи корзины, относящиеся к этому товару (без модификаторов или с суффиксом ::…). */
export const getCartLineKeysForProduct = (
  cart: Record<string, CartProduct>,
  productId: string
): string[] => {
  const id = String(productId)
  return Object.keys(cart).filter(k => k === id || k.startsWith(`${id}::`)).sort()
}

/**
 * Восстанавливает модификаторы из ключа корзины (формат buildCartKey: id::1:2,3:1).
 * Нужно после перезагрузки, если в строке из localStorage пустой modifiers.
 */
export const parseModifiersFromCartKey = (
  cartKey: string,
  productId: string
): Array<{ id: number; amount: number }> => {
  const id = String(productId)
  const prefix = `${id}::`
  if (!cartKey.startsWith(prefix)) return []
  const rest = cartKey.slice(prefix.length)
  if (!rest) return []
  return rest
    .split(',')
    .map(part => {
      const [mid, amt] = part.split(':')
      const n = Number(mid)
      const amount = Number(amt) || 0
      if (Number.isNaN(n) || amount <= 0) return null
      return { id: n, amount }
    })
    .filter((m): m is { id: number; amount: number } => m != null)
}

/** Суммарное количество по всем строкам корзины для товара (разные наборы модификаторов — разные ключи). */
export const sumCartCountForProduct = (
  cart: Record<string, CartProduct>,
  productId: string
): number => {
  return getCartLineKeysForProduct(cart, productId).reduce(
    (sum, key) => sum + (cart[key]?.count ?? 0),
    0
  )
}

/**
 * Те же дефолтные модификаторы, что в ClientProductDetail.initializeDefaultModifiers
 * (минимумы группы, group.down, item.down и добор до group.down).
 */
/**
 * @param availabilityOverrides — карта id → available из API /availability.
 *   undefined (нет ключа) → использовать item.available из меню;
 *   null → безлимитно;  0 → недоступен;  >0 → лимит.
 */
export const computeDefaultModifierRowsForProduct = (
  product: QrMenuProduct,
  availabilityOverrides?: Partial<Record<number, number | null>>
): Array<{ id: number; groupId: number; amount: number }> => {
  if (!product.modifiers?.length) return []

  const isItemUnavailable = (item: QrMenuModifier): boolean => {
    const override = availabilityOverrides?.[item.id]
    if (override !== undefined) return override === 0
    return item.available === 0
  }

  /** Эффективный лимит элемента с учётом API-override */
  const effectiveItemCap = (item: QrMenuModifier): number => {
    const override = availabilityOverrides?.[item.id]
    const avail = override !== undefined ? override : item.available
    const up = item.up || Infinity
    if (avail != null && avail > 0) return Math.min(up, avail)
    return up
  }

  const next: Array<{ id: number; groupId: number; amount: number }> = []

  for (const group of product.modifiers) {
    const groupMax = group.up || Infinity
    const groupMin = Math.max(0, group.down || 0)

    let remainingGroupCapacity = groupMax
    const assigned: number[] = new Array(group.items.length).fill(0)

    // Фаза 1: обязательные минимумы каждого элемента (down > 0), скипаем недоступные
    for (let i = 0; i < group.items.length; i++) {
      const item = group.items[i]
      if (isItemUnavailable(item)) continue
      const itemMin = Math.max(0, item.down || 0)
      if (itemMin <= 0) continue
      const itemCap = effectiveItemCap(item)
      const assignDown = Math.min(itemMin, itemCap, remainingGroupCapacity)
      if (assignDown > 0) {
        assigned[i] = assignDown
        if (remainingGroupCapacity !== Infinity) remainingGroupCapacity -= assignDown
        if (remainingGroupCapacity === 0) break
      }
    }

    // Фаза 2: добиваем до groupMin первыми доступными элементами
    const currentTotal = assigned.reduce((sum, n) => sum + n, 0)
    let needToReachMin = Math.max(0, groupMin - currentTotal)

    while (needToReachMin > 0 && remainingGroupCapacity > 0) {
      let addedThisRound = false
      for (let i = 0; i < group.items.length; i++) {
        if (needToReachMin === 0 || remainingGroupCapacity === 0) break
        const item = group.items[i]
        if (isItemUnavailable(item)) continue
        const itemCap = effectiveItemCap(item)
        if (assigned[i] < itemCap) {
          assigned[i] += 1
          addedThisRound = true
          needToReachMin -= 1
          if (remainingGroupCapacity !== Infinity) remainingGroupCapacity -= 1
        }
      }
      if (!addedThisRound) break
    }

    for (let i = 0; i < group.items.length; i++) {
      const amount = assigned[i]
      if (amount > 0) {
        next.push({ id: group.items[i].id, groupId: group.id, amount })
      }
    }
  }

  return next
}

/** Компактный вид для стора / buildCartKey — как в watch ClientProductDetail. */
export const compactModifierRowsForStore = (
  rows: Array<{ id: number; groupId: number; amount: number }>
): Array<{ id: number; amount: number }> =>
  rows.filter(m => m.amount > 0).map(m => ({ id: m.id, amount: m.amount }))

export const computeDefaultModifiersForProduct = (
  product: QrMenuProduct
): Array<{ id: number; amount: number }> =>
  compactModifierRowsForStore(computeDefaultModifierRowsForProduct(product))

/**
 * Тот же источник модификаторов, что при работе из модалки детали:
 * 1) selectedModifiersByProduct из стора (после детали / прошлого клика),
 * 2) иначе одна строка в корзине — её модификаторы,
 * 3) иначе дефолты как в детали.
 */
export const getEffectiveModifiersForProduct = (
  product: QrMenuProduct,
  selectedByProduct: Record<string, Array<{ id: number; amount: number }>>,
  cart: Record<string, CartProduct>
): Array<{ id: number; amount: number }> => {
  const stored = selectedByProduct[product.id]
  if (stored !== undefined) return stored

  const keys = getCartLineKeysForProduct(cart, product.id)
  if (keys.length >= 1) {
    const cartKey = keys[0]
    const line = cart[cartKey]
    let mods = (line?.modifiers ?? [])
      .filter(m => (m?.amount ?? 0) > 0)
      .map(m => ({ id: m.id, amount: m.amount ?? 0 }))
    if (mods.length === 0 && cartKey.includes('::')) {
      mods = parseModifiersFromCartKey(cartKey, product.id)
    }
    return mods
  }

  return computeDefaultModifiersForProduct(product)
}

/** Количество на карточке: как в детали (один ключ), либо сумма при нескольких строках. */
export const getCartCountForProductLikeDetail = (
  product: QrMenuProduct,
  selectedByProduct: Record<string, Array<{ id: number; amount: number }>>,
  cart: Record<string, CartProduct>
): number => {
  const keys = getCartLineKeysForProduct(cart, product.id)
  if (keys.length > 1) return sumCartCountForProduct(cart, product.id)
  if (keys.length === 1) {
    return cart[keys[0]]?.count ?? 0
  }
  const mods = getEffectiveModifiersForProduct(product, selectedByProduct, cart)
  const key = buildCartKey(product, mods)
  return cart[key]?.count ?? 0
}

export const setCartProduct = (
  product: QrMenuProduct,
  selectedModifiers: Array<{ id: number; amount: number }>,
  prev?: CartProduct,
  deltaCount: number = 0
): CartProduct => {
  const nextCount = Math.max(0, (prev?.count ?? 0) + deltaCount)
  const key = prev?.key ?? buildCartKey(product, selectedModifiers)
  return {
    key,
    id: product.id,
    count: nextCount,
    modifiers: selectedModifiers
  }
}

export const setCartProductFromStorage = (
  product: QrMenuProduct,
  modifiers: Array<{ id: number; amount: number; name: string }>,
  options?: { count?: number; price?: number; key?: string }
): CartProductFromStorage => {
  return {
    key: options?.key ?? buildCartKey(product, modifiers.map(m => ({ id: m.id, amount: m.amount }))),
    name: product.name,
    imageUrl: product.imageUrl ?? '',
    price: options?.price ?? product.price,
    id: product.id,
    count: options?.count ?? 1,
    modifiers: modifiers.map(m => ({
      id: m.id,
      amount: m.amount ?? 0,
      name: m.name
    }))
  }
}

export const buildProductsIndex = (products: Array<QrMenuProduct>) => {
  const byId = new Map<string, QrMenuProduct>()
  const modifierNameById = new Map<number, string>()
  const modifierPriceById = new Map<number, number>()

  for (const p of products) {
    byId.set(p.id, p)
    for (const group of p.modifiers ?? []) {
      for (const item of group.items ?? []) {
        modifierNameById.set(item.id, item.name)
        modifierPriceById.set(item.id, item.price ?? 0)
      }
    }
  }

  return { byId, modifierNameById, modifierPriceById }
}

export const calcCartItemUnitPrice = (
  product: QrMenuProduct | undefined,
  cartItem: CartProduct
): number => {
  if (!product) return 0
  const basePrice = product.price ?? 0
  const priceByModifierId: Record<number, number> = {}
  for (const group of product.modifiers ?? []) {
    for (const item of group.items) priceByModifierId[item.id] = item.price ?? 0
  }
  const modifiersSum = (cartItem.modifiers ?? []).reduce((sum, m) => {
    const price = priceByModifierId[m.id] ?? 0
    return sum + price * (m.amount ?? 0)
  }, 0)
  return basePrice + modifiersSum
}

export const buildQrMenuOrderItemsFromCart = (
  cartProducts: CartProductFromStorage[],
  flatProducts: Array<QrMenuProduct & { categoryId?: string | number }>
): QrMenuOrderItem[] => {
  const productById = new Map<string, QrMenuProduct & { categoryId?: string | number }>(
    flatProducts.map(p => [p.id, p])
  )

  return cartProducts.map(cp => {
    const details = productById.get(cp.id) || ({} as QrMenuProduct & { categoryId?: string | number })

    return {
      id: cp.id,
      product_id: details?.product_id ?? '',
      category: { id: String(details?.categoryId ?? '') },
      name: cp.name,
      description: details?.description ?? '',
      price: cp.price,
      unit: details?.unit ?? '',
      count: cp.count,
      imageUrl: details?.imageUrl ?? '',
      bigImageUrl: details?.bigImageUrl ?? '',
      spic: details?.spic ?? '',
      code: details?.code ?? '',
      weight: Number(details?.weight ?? 0) || 0,
      modifiers: (cp.modifiers || []).map(m => ({
        id: String(m.id),
        name: m.name,
        price: 0,
        count: m.amount
      }))
    }
  })
}

export const buildCreateOrderPayload = (
  menuId: string,
  storeId: string,
  cartProducts: CartProductFromStorage[],
  flatProducts: Array<QrMenuProduct & { categoryId?: string | number }>,
  totalPrice: number,
  orderType: QrMenuOrderType = 'takeaway',
  orderComment?: string,
  paymentMethod?: string,
  cardToken?: string | null
): QrMenuCreateOrderPayload => {
  const items = buildQrMenuOrderItemsFromCart(cartProducts, flatProducts)

  const payload: QrMenuCreateOrderPayload = {
    menu_id: String(menuId),
    table_id: null,
    store_id: String(storeId),
    type: orderType,
    price: totalPrice,
    items,
    without_request: false
  }
  const c = orderComment?.trim()
  if (c) payload.comment = c
  const pm = paymentMethod?.trim()
  if (pm) payload.payment_system = pm
  const ct = cardToken?.trim()
  if (ct) payload.card = { token: ct }
  return payload
}

/** POST `/menu/table-order` — `tableId` из query `t` */
export const buildTableOrderPayload = (
  menuId: string,
  tableId: string,
  cartProducts: CartProductFromStorage[],
  flatProducts: Array<QrMenuProduct & { categoryId?: string | number }>,
  totalPrice: number,
  orderComment?: string,
  paymentMethod?: string,
  cardToken?: string | null
): QrMenuTableOrderPayload => {
  const items = buildQrMenuOrderItemsFromCart(cartProducts, flatProducts)
  const payload: QrMenuTableOrderPayload = {
    menu_id: String(menuId),
    table_id: String(tableId).trim(),
    type: 'on_table',
    price: totalPrice,
    items
  }
  const c = orderComment?.trim()
  if (c) payload.comment = c
  const pm = paymentMethod?.trim()
  if (pm) payload.payment_system = pm
  const ct = cardToken?.trim()
  if (ct) payload.card = { token: ct }
  return payload
}

export const setItemFromCheck = (item: QrMenuCheckProduct): QrMenuCheckProduct => {
  return {
    id: String(item?.id ?? ''),
    name: item?.name ?? '',
    description: item?.description ?? '',
    price: Number(item?.price ?? 0),
    image: item?.image ?? '',
    bigImageUrl: item?.bigImageUrl ?? '',
    count: Number(item?.count ?? 0),
    modifiers: (item?.modifiers ?? []).map(m => ({
      id: Number(m?.id ?? 0),
      count: Number(m?.count ?? 0),
      name: m?.name ?? ''
    }))
  }
}

export const convertCheckItemToCartProduct = (
  item: QrMenuCheckProduct
): CartProductFromStorage => {
  return {
    key: String(item.id),
    name: item.name,
    imageUrl: item.image,
    price: Number(item.price ?? 0),
    id: String(item.id),
    count: Number(item.count ?? 0),
    modifiers: (item.modifiers ?? []).map(m => ({
      id: Number(m.id ?? 0),
      amount: Number(m.count ?? 0),
      name: m.name ?? ''
    }))
  }
}

/** id для POST `/menu/payment/{id}`: приоритет `payment.id`, иначе `payment_id` */
export const getOrderPaymentResourceId = (p: QrMenuFastOrderPayment): string | null => {
  const raw = p.id ?? p.payment_id
  if (raw == null) return null
  const s = String(raw).trim()
  return s || null
}

const mapFastOrderApiItemToCheckProduct = (item: QrMenuFastOrderApiOrderItem): QrMenuCheckProduct => ({
  id: String(item?.id ?? ''),
  name: item?.name ?? '',
  description: item?.description ?? '',
  price: Number(item?.price ?? 0),
  image: item?.image ?? item?.imageUrl ?? '',
  bigImageUrl: item?.bigImageUrl ?? '',
  count: Number(item?.count ?? 0),
  modifiers: (item?.modifiers ?? []).map(m => ({
    id: Number(m?.id ?? 0),
    count: Number(m?.count ?? 0),
    name: m?.name ?? ''
  }))
})

/** Собирает `QrMenuCheckDetails` из фактического тела `data` POST `/menu/fast-order` (`order` + `payment`). */
export const mapFastOrderResponseToCheckDetails = (data: QrMenuFastOrderResponseData): QrMenuCheckDetails => {
  const o = data.order
  const d = o.details ?? {}
  const statusRaw =
    typeof d.status === 'number'
      ? d.status
      : typeof o.status === 'number'
        ? o.status
        : QrMenuCheckDetailsType.STATUS_DRAFT
  const status = Number.isFinite(statusRaw)
    ? (statusRaw as QrMenuCheckDetailsType)
    : QrMenuCheckDetailsType.STATUS_DRAFT

  const items = (d.items ?? []).map(item => setItemFromCheck(mapFastOrderApiItemToCheckProduct(item)))

  const orderNumberFromApi = o.order_number
  const orderNumberNormalized =
    orderNumberFromApi != null && String(orderNumberFromApi).trim() !== ''
      ? String(orderNumberFromApi).trim()
      : null

  return {
    type: o.type ?? 'spot',
    uniqid: o.uid ?? '',
    number: String(o.number ?? ''),
    order_id: String(o.id ?? o.uuid ?? ''),
    order_number: orderNumberNormalized,
    uuid: o.uuid ?? null,
    payment: data.payment ?? null,
    details: {
      guid: null,
      orderSum: Number(d.orderSum ?? o.total_price ?? 0),
      unpaidSum: Number(d.unpaidSum ?? 0),
      discountSum: Number(d.discountSum ?? 0),
      totalPieces: Number(d.totalPieces ?? 0),
      number: d.number ?? null,
      paid: o.paid ? 1 : 0,
      finished: o.finished ? 1 : 0,
      status,
      id: d.id != null ? String(d.id) : null,
      code: d.code ?? null,
      items,
      employee: { id: null, code: null, name: null },
      table: {
        id: d.table?.id ?? (o.table_id != null ? String(o.table_id) : null),
        code: d.table?.code ?? null,
        name: d.table?.name ?? null
      },
      cashdesk: {
        id: d.cashdesk?.id ?? null,
        code: d.cashdesk?.code ?? null,
        name: d.cashdesk?.name ?? null
      }
    }
  }
}

export const setCheckDetails = (data: QrMenuCheckDetails) => {
  return {
    type: data?.type ?? 'on_table',
    uniqid: data?.uniqid ?? '',
    number: data?.number ?? '',
    order_id: data?.order_id ?? data?.uuid ?? data?.uniqid ?? '',
    order_number: data?.order_number ?? null,
    uuid: data?.uuid ?? null,
    payment: data?.payment ?? null,
    details: {
      guid: null,
      orderSum: Number(data?.details?.orderSum ?? 0),
      unpaidSum: Number(data?.details?.unpaidSum ?? 0),
      discountSum: Number(data?.details?.discountSum ?? 0),
      totalPieces: Number(data?.details?.totalPieces ?? 0),
      number: data?.details?.number ?? null,
      paid: Number(data?.details?.paid ?? 0),
      finished: Number(data?.details?.finished ?? 0),
      status: data?.details?.status ?? QrMenuCheckDetailsType.STATUS_DRAFT,
      id: data?.details?.id ?? null,
      code: data?.details?.code ?? null,
      items: data?.details?.items?.map(item => setItemFromCheck(item)) ?? [],
      employee: {
        id: null,
        code: null,
        name: null
      },
      table: {
        id: data?.details?.table?.id ?? null,
        code: data?.details?.table?.code ?? null,
        name: data?.details?.table?.name ?? null
      },
      cashdesk: {
        id: data?.details?.cashdesk?.id ?? null,
        code: data?.details?.cashdesk?.code ?? null,
        name: data?.details?.cashdesk?.name ?? null
      }
    }
  }
}